import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["modal", "progressBar", "progressStatus", "processedCount", "totalCount", "successfulCount", "failedCount", "titleIcon"]
  static values = { 
    progressId: String,
    appId: String,
    progressUrl: String
  }

  connect() {
    if (this.progressIdValue) {
      this.showModal()
      this.startProgressTracking()
    }

    this.viewProgressHandler = this.handleViewProgress.bind(this)
    document.addEventListener('iframe-progress:view', this.viewProgressHandler)
  }

  disconnect() {
    if (this.progressInterval) {
      clearInterval(this.progressInterval)
    }

    document.removeEventListener('iframe-progress:view', this.viewProgressHandler)
  }

  // Allows other parts of the page (e.g. the configuration history list) to
  // reopen this modal for a past progress record, even if it isn't the one
  // that was live when the page loaded.
  handleViewProgress(event) {
    const progressId = event.detail && event.detail.progressId
    if (!progressId) {
      return
    }

    this.stopProgressTracking()
    this.progressIdValue = String(progressId)
    this.showModal()
    this.startProgressTracking()
  }

  showModal() {
    const modal = new bootstrap.Modal(this.modalTarget)
    modal.show()
  }

  startProgressTracking() {
    if (this.progressInterval) {
      clearInterval(this.progressInterval)
    }

    // Start polling for progress updates
    this.progressInterval = setInterval(() => {
      this.fetchProgress()
    }, 2000) // Poll every 2 seconds

    // Initial fetch
    this.fetchProgress()
  }

  async fetchProgress() {
    try {
      let response = await fetch(`${this.progressUrlValue}?progress_id=${this.progressIdValue}&app_id=${this.appIdValue}`)
      
      if (!response.ok) {
        if (response.status === 404) {
          this.handleProgressNotFound()
        }
        return
      }

      const data = await response.json()
      this.updateProgressUI(data)
      
      // Stop polling if completed or failed
      if (data.status === 'completed' || data.status === 'failed') {
        this.stopProgressTracking()
        
        // Auto-close modal after 3 seconds if completed
        if (false && data.status === 'completed') {
          setTimeout(() => {
            this.closeModal()
          }, 3000)
        }
      }
    } catch (error) {
      console.error('Error fetching progress:', error)
    }
  }

  updateProgressUI(data) {
    // Update modal progress bar
    this.progressBarTarget.style.width = `${data.progress_percentage}%`
    this.progressBarTarget.className = `progress-bar ${this.getProgressBarClass(data.status)}`

    // Update modal counts
    this.processedCountTarget.textContent = data.processed_locations || 0
    this.totalCountTarget.textContent = data.total_locations || 0
    this.successfulCountTarget.textContent = data.successful_locations || 0
    this.failedCountTarget.textContent = data.failed_locations || 0

    // Update status badge
    const statusClass = this.getStatusClass(data.status)
    const statusText = this.getStatusText(data.status)
    
    this.progressStatusTarget.className = `badge ${statusClass}`
    this.progressStatusTarget.textContent = statusText

    this.updateTitleIcon(data.status)
  }

  updateTitleIcon(status) {
    if (!this.hasTitleIconTarget) {
      return
    }

    const icon = this.titleIconTarget
    icon.classList.remove('fa-spinner', 'fa-spin', 'fa-check-circle', 'fa-times-circle', 'text-success', 'text-danger')

    switch (status) {
      case 'completed':
        icon.classList.add('fa-check-circle', 'text-success')
        break
      case 'failed':
        icon.classList.add('fa-times-circle', 'text-danger')
        break
      default:
        icon.classList.add('fa-spinner', 'fa-spin')
    }
  }

  getStatusClass(status) {
    switch(status) {
      case 'pending': return 'border border-secondary text-secondary bg-transparent'
      case 'in_progress': return 'bg-secondary'
      case 'completed': return 'bg-success'
      case 'failed': return 'bg-danger'
      default: return 'border border-secondary text-secondary bg-transparent'
    }
  }

  getProgressBarClass(status) {
    switch(status) {
      case 'in_progress': return 'bg-secondary progress-bar-striped progress-bar-animated'
      case 'completed': return 'bg-success'
      case 'failed': return 'bg-danger'
      default: return 'bg-secondary'
    }
  }

  getStatusText(status) {
    switch(status) {
      case 'pending': return 'Pending'
      case 'in_progress': return 'In Progress'
      case 'completed': return 'Completed'
      case 'failed': return 'Failed'
      default: return 'Initializing...'
    }
  }

  handleProgressNotFound() {
    this.stopProgressTracking()
    this.updateProgressUI({ 
      status: 'failed', 
      error_message: 'Progress record not found' 
    })
  }

  stopProgressTracking() {
    if (this.progressInterval) {
      clearInterval(this.progressInterval)
      this.progressInterval = null
    }
  }

  closeModal() {
    const modal = bootstrap.Modal.getInstance(this.modalTarget)
    if (modal) {
      modal.hide()
    }
  }

  refresh() {
    if (this.progressIdValue) {
      this.fetchProgress()
    }
  }
} 